import { Component } from '@nicolas-riciotti/light2'

/**
 * <newsletter-form> — newsletter signup (Ortto via the hr/v1/subscribe REST route).
 *
 * Markup: template-parts/newsletter-form.php. Server: includes/newsletter.php.
 *
 * - The country select drives the consent block: which extra legal wording is
 *   shown and whether the checkbox is required (implied-consent countries get
 *   the statement only).
 * - Google reCAPTCHA v2 is loaded lazily, the first time the form scrolls into
 *   view or receives focus, so the third-party script stays off the critical path.
 * - Submit posts urlencoded form data to data-endpoint and expects
 *   { success: boolean, message: string }.
 */

const RECAPTCHA_SRC = 'https://www.google.com/recaptcha/api.js?render=explicit&onload=__hrRecaptchaOnload'
const CHECKBOX_VARIANTS = new Set(['checkbox', 'checkbox_checked', 'korea', 'canada', 'china'])

let recaptchaPromise = null

// ––––– reveal motion: things that appear mid-form open their height and fade in rather than jumping in.

const EASE_OUT = 'cubic-bezier(0.22, 1, 0.36, 1)'
const prefersReducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

// Unhides $el, growing it from zero height (its top margin too, so nothing below jumps).
function revealElement($el, delay = 0) {
    if (!$el || !$el.hidden) return
    $el.hidden = false
    if (prefersReducedMotion() || typeof $el.animate !== 'function') return

    const height = $el.offsetHeight
    const marginTop = getComputedStyle($el).marginTop
    $el.style.overflow = 'hidden'
    const animation = $el.animate([
        { height: '0px', marginTop: '0px', opacity: 0, transform: 'translateY(-8px)' },
        { height: `${height}px`, marginTop, opacity: 1, transform: 'none' },
    ], { duration: 550, delay, easing: EASE_OUT, fill: 'backwards' })
    animation.onfinish = animation.oncancel = () => { $el.style.overflow = '' }
}

// Runs change() and animates $el from its old height to the new one.
function morphHeight($el, change) {
    if (!$el || $el.hidden || prefersReducedMotion() || typeof $el.animate !== 'function') {
        change()
        return
    }
    const from = $el.offsetHeight
    change()
    const to = $el.offsetHeight
    if (from === to) return

    $el.style.overflow = 'hidden'
    const animation = $el.animate([{ height: `${from}px` }, { height: `${to}px` }], { duration: 400, easing: EASE_OUT })
    animation.onfinish = animation.oncancel = () => { $el.style.overflow = '' }
}

function loadRecaptcha() {
    if (window.grecaptcha?.render) return Promise.resolve(window.grecaptcha)
    if (recaptchaPromise) return recaptchaPromise

    recaptchaPromise = new Promise((resolve, reject) => {
        window.__hrRecaptchaOnload = () => resolve(window.grecaptcha)
        const script = document.createElement('script')
        script.src = RECAPTCHA_SRC
        script.async = true
        script.defer = true
        script.onerror = () => {
            recaptchaPromise = null
            reject(new Error('reCAPTCHA failed to load'))
        }
        document.head.appendChild(script)
    })
    return recaptchaPromise
}

export default class NewsletterForm extends Component {

    created() {
        this.widgetId = null
        this.isSubmitting = false
    }

    attached() {
        this.$form          = this.querySelector('[newsletter-form-el]')
        this.$email         = this.querySelector('input[name="email"]')
        this.$country       = this.querySelector('select[name="country"]')
        this.$consent       = this.querySelector('[newsletter-consent]')
        this.$consentInput  = this.querySelector('[newsletter-consent-input]')
        this.$consentExtras = this.querySelectorAll('[newsletter-consent-extra]')
        this.$recaptcha     = this.querySelector('[newsletter-recaptcha]')
        this.$recaptchaWrap = this.querySelector('[newsletter-recaptcha-wrap]')
        this.$submit        = this.querySelector('[newsletter-submit]')
        this.$status        = this.querySelector('[newsletter-status]')
        this.$success       = this.querySelector('[newsletter-success]')
        this.$reveal        = this.querySelectorAll('[newsletter-reveal]')

        if (!this.$form) return

        this.$country?.addEventListener('change', () => this.onCountryChange())
        this.armReveal()
        this.$form.addEventListener('submit', e => {
            e.preventDefault()
            e.stopPropagation()
            this.onSubmit()
        })

        // Clear a stale error as soon as the visitor edits something.
        this.$form.addEventListener('input', () => {
            if (this.$status?.dataset.kind === 'error') this.setStatus('')
        })

        this.armRecaptchaLoading()

        // Same-page "#subscribe" links (header + mobile menu) scroll via Lenis without a
        // navigation, so the open mobile menu would otherwise stay put over the footer.
        document.querySelectorAll('a[href$="#subscribe"]').forEach($link => {
            $link.addEventListener('click', () => {
                const header = document.querySelector('app-header')
                if (header?.isMenuOpened && typeof header.closeMenu === 'function') header.closeMenu()
            })
        })

        // Arriving with #subscribe in the URL (e.g. from the home page, which has no footer):
        // Lenis suppresses the browser's native hash jump, so scroll here once the page settles.
        if (window.location.hash === '#subscribe') {
            setTimeout(() => this.scrollIntoViewSmooth(), 600)
        }
    }

    scrollIntoViewSmooth() {
        const target = document.getElementById('subscribe') || this
        const header = document.querySelector('app-header')
        const offset = header ? -(header.getBoundingClientRect().bottom + 20) : -120
        const lenis = window.Lenis || window.lenis
        if (typeof lenis?.scrollTo === 'function') lenis.scrollTo(target, { offset })
        else target.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }

    // ––––– consent

    // Inline variant: the country select stays hidden until the visitor has typed an email.
    armReveal() {
        if (!this.$reveal.length || !this.$email) return
        const reveal = () => {
            if (this.$email.value.trim() === '') return
            this.$email.removeEventListener('input', reveal)
            this.$reveal.forEach(($el, i) => revealElement($el, i * 80))
        }
        this.$email.addEventListener('input', reveal)
        reveal()
    }

    onCountryChange() {
        const option  = this.$country.selectedOptions?.[0]
        // Before a country is picked the inline variant shows the generic (implied) statement.
        const variant = option?.dataset.consent || this.dataset.consentDefault || ''

        if (!variant) {
            this.$consent.hidden = true
            return
        }

        const apply = () => {
            this.$consentExtras.forEach($el => {
                $el.hidden = $el.getAttribute('newsletter-consent-extra') !== variant
            })

            const needsCheckbox = CHECKBOX_VARIANTS.has(variant)
            if (this.$consentInput) {
                this.$consentInput.required = needsCheckbox
                this.$consentInput.checked  = variant === 'checkbox_checked'
                // Implied-consent countries show the statement only: hide the box, keep the text.
                this.$consentInput.classList.toggle('hidden', !needsCheckbox)
                this.$consentInput.closest('[newsletter-consent-label]')?.classList.toggle('cursor-pointer', needsCheckbox)
            }
        }

        // Already showing (inline variant, or a second country pick): ease to the new wording's height.
        if (!this.$consent.hidden) {
            morphHeight(this.$consent, apply)
            return
        }
        apply()
        revealElement(this.$consent)
    }

    // ––––– reCAPTCHA
    //
    // Google's script is fetched only when the email field gains focus, and the
    // checkbox is revealed only once the visitor has typed an address, so the
    // widget never appears before it is needed.

    armRecaptchaLoading() {
        if (!this.$recaptcha || !this.dataset.sitekey || !this.$email) return

        this.$email.addEventListener('focusin', () => { loadRecaptcha().catch(() => {}) }, { once: true })

        const reveal = () => {
            if (this.$email.value.trim() !== '') {
                this.$email.removeEventListener('input', reveal)
                this.renderRecaptcha()
            }
        }
        this.$email.addEventListener('input', reveal)
        // Browser autofill can populate the field before any input event.
        reveal()
    }

    async renderRecaptcha() {
        if (this.widgetId !== null) return
        try {
            const grecaptcha = await loadRecaptcha()
            if (this.widgetId !== null) return
            this.widgetId = grecaptcha.render(this.$recaptcha, {
                sitekey: this.dataset.sitekey,
                callback: () => { if (this.$status?.dataset.kind === 'error') this.setStatus('') },
            })
            // A beat after the country select, which opens on the same keystroke.
            revealElement(this.$recaptchaWrap, 160)
        }
        catch (err) {
            this.setStatus(this.dataset.errorMessage || 'Something went wrong. Please try again in a moment.', 'error')
        }
    }

    recaptchaToken() {
        if (this.widgetId === null || !window.grecaptcha) return ''
        try { return window.grecaptcha.getResponse(this.widgetId) || '' }
        catch { return '' }
    }

    resetRecaptcha() {
        if (this.widgetId === null || !window.grecaptcha) return
        try { window.grecaptcha.reset(this.widgetId) } catch { /* noop */ }
    }

    // ––––– submit

    validate() {
        const email = (this.$email?.value || '').trim()
        if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            this.$email?.focus()
            return 'Please enter a valid email address.'
        }
        if (!this.$country?.value) {
            this.$reveal.forEach($el => revealElement($el))
            this.$country?.focus()
            return 'Please select your country.'
        }
        if (this.$consentInput?.required && !this.$consentInput.checked) {
            this.$consentInput.focus()
            return 'Please agree to receive the newsletter.'
        }
        if (!this.recaptchaToken()) {
            // Widget may not be rendered yet (very fast visitor, or script still loading).
            this.renderRecaptcha()
            return 'Please confirm you are not a robot.'
        }
        return ''
    }

    async onSubmit() {
        if (this.isSubmitting) return

        const problem = this.validate()
        if (problem) {
            this.setStatus(problem, 'error')
            return
        }

        this.setBusy(true)
        this.setStatus('')
        window.clarity?.('set', 'newsletter', 'submit')

        const formData = new FormData(this.$form)
        formData.set('email', (formData.get('email') || '').toString().trim())
        formData.set('g-recaptcha-response', this.recaptchaToken())

        try {
            const res = await fetch(this.dataset.endpoint || '/wp-json/hr/v1/subscribe', {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
                    'Accept': 'application/json',
                },
                body: new URLSearchParams([...formData.entries()]).toString(),
            })
            const data = await res.json().catch(() => null)

            if (res.ok && data?.success) {
                this.showSuccess(data.message)
                window.clarity?.('set', 'newsletter', 'success')
            }
            else {
                // Which server check failed (honeypot, recaptcha, ortto, …), or the HTTP status if no JSON came back.
                const code = data?.code || `http-${res.status}`
                console.warn('[newsletter] signup failed:', code)
                this.setStatus(this.withDebugCode(data?.message || this.dataset.errorMessage || 'Something went wrong. Please try again in a moment.', code), 'error')
                this.resetRecaptcha()
                window.clarity?.('set', 'newsletter', 'error')
            }
        }
        catch (err) {
            console.warn('[newsletter] signup request failed:', err)
            this.setStatus(this.withDebugCode(this.dataset.errorMessage || 'Something went wrong. Please try again in a moment.', `network: ${err?.name || err}`), 'error')
            this.resetRecaptcha()
            window.clarity?.('set', 'newsletter', 'error')
        }
        finally {
            this.setBusy(false)
        }
    }

    // data-debug (every host but production): append the failure code so it can be read off a phone.
    withDebugCode(message, code) {
        return this.hasAttribute('data-debug') ? `${message} [${code}]` : message
    }

    showSuccess(message) {
        if (message && this.$success) this.$success.textContent = message
        this.$form.hidden = true
        if (this.$success) {
            this.$success.hidden = false
            this.$success.focus({ preventScroll: true })
        }
    }

    setBusy(busy) {
        this.isSubmitting = busy
        if (this.$submit) {
            this.$submit.disabled = busy
            this.$submit.setAttribute('aria-busy', busy ? 'true' : 'false')
            this.$submit.classList.toggle('opacity-50', busy)
        }
    }

    setStatus(message, kind = '') {
        if (!this.$status) return
        this.$status.textContent = message
        this.$status.dataset.kind = kind
        this.$status.classList.toggle('text-[#B3261E]', kind === 'error')
    }
}

customElements.define('newsletter-form', NewsletterForm)
